import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Eye, EyeOff, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { MicrosoftSignInCard } from "../components/MicrosoftSignInCard";
import axios from "axios";
import { API } from "../config/api";
import { LOGO_URL } from "../config/constants";

// BM-H267 (03 Sep 2026): conflict approval links now require a session, so a
// director arriving from an approval email lands here first. The page they
// were trying to reach is stashed in sessionStorage by the caller and
// consumed exactly once, so a successful sign-in drops them back on the
// approval instead of the dashboard. Only same-origin absolute paths are
// honoured, so a crafted value cannot bounce anyone off-site.
export const POST_LOGIN_REDIRECT_KEY = "bonsai_post_login_redirect";

export function takePostLoginRedirect(fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const target = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    if (target && target.startsWith("/") && !target.startsWith("//")) {
      return target;
    }
  } catch {
    // Storage unavailable (private mode). Fall through to the default.
  }
  return fallback;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showCredentialLogin, setShowCredentialLogin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Fix 6 — banner shown when the axios interceptor redirects us here
  // after a SESSION_EXPIRED 401. Sourced from ?session_expired=1.
  // eslint-disable-next-line no-unused-vars
  const [sessionExpired, setSessionExpired] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("session_expired") === "1";
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();

  // Log on mount to help debug auth flow
  useEffect(() => {
    
    // Check if returning from MS OAuth (callback redirected here with params)
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get("auth");
    const bonsaiUserId = urlParams.get("bonsai_user_id");
    
    if (authSuccess === "success" && bonsaiUserId) {
      // Store auth state so ProtectedRoute allows access
      localStorage.setItem("bonsai_auth", "true");
      localStorage.setItem("bonsai_last_activity", Date.now().toString());
      sessionStorage.setItem("bonsai_user_id", bonsaiUserId);
      // Full page reload so AuthProvider re-reads localStorage on mount
      window.location.href = takePostLoginRedirect("/bonsai");
      return;
    }
    
    // Auto-redirect: If user already has a valid session, send them to /bonsai
    const storedUserId = sessionStorage.getItem("bonsai_user_id");
    // Also check cookie as fallback
    const cookieMatch = document.cookie.match(/bonsai_user_id=([^;]+)/);
    const effectiveUserId = storedUserId || (cookieMatch ? cookieMatch[1] : null);
    
    if (effectiveUserId) {
      if (!storedUserId && cookieMatch) {
        sessionStorage.setItem("bonsai_user_id", cookieMatch[1]);
      }
      axios.get(`${API}/bonsai/auth/status`, {
        headers: { "X-Bonsai-User-Id": effectiveUserId },
        timeout: 10000
      }).then(res => {
        if (res.data.authenticated) {
          localStorage.setItem("bonsai_auth", "true");
          window.location.href = takePostLoginRedirect("/bonsai");
        } else {
        }
      }).catch(err => {
        console.log("[LoginPage] Auth status check failed:", err.message);
      });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Try email/password login first
      const response = await axios.post(`${API}/auth/login`, {
        email: email,
        password: password
      });
      
      if (response.data.success) {
        // Store user info in localStorage
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("token", response.data.token);
        
        // Set auth state in localStorage (AuthProvider reads from localStorage)
        localStorage.setItem("bonsai_auth", "true");
        localStorage.setItem("bonsai_last_activity", Date.now().toString());
        
        // Set bonsai_user_id for BonsaiContext auth checks (in both storages)
        if (response.data.user?.email || email) {
          const userId = response.data.user?.email || email;
          sessionStorage.setItem("bonsai_user_id", userId);
          localStorage.setItem("bonsai_user_id", userId);
        }
        
        // Navigate and reload to pick up auth state
        const target = takePostLoginRedirect("/");
        if (target === "/") {
          navigate("/");
          window.location.reload();
        } else {
          window.location.href = target;
        }
      } else {
        setError(response.data.message || "Invalid credentials");
      }
    } catch (err) {
      // Issue #226: shared-password fallback removed — every login must
      // carry an email so the backend can attribute the session to a
      // named user. No silent fallback path.
      setError("Invalid email or password");
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Clean Branding */}
      {/* BM-H3269: this brand panel stays the original teal by request (29 Sep 2026). */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0d9488] relative overflow-hidden items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1512428813834-c702c7702b78?w=1200')"
          }}
        />
        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />
        <div className="relative z-10 text-center px-12">
          <img 
            src={LOGO_URL}
            alt="Bonsai Law" 
            className="w-36 h-36 mx-auto mb-8 drop-shadow-2xl"
            data-testid="login-logo"
          />
          <h1 className="text-4xl text-white mb-4">
            Bonsai CaseMinder
          </h1>
          <p className="text-base text-white/60 max-w-sm mx-auto leading-relaxed">
            Matter management and administration platform for Bonsai Law
          </p>
          <div className="mt-10 flex items-center justify-center gap-6 text-white/40 text-xs tracking-wider uppercase">
            <span>Compliance</span>
            <span className="w-1 h-1 rounded-md bg-white/30" />
            <span>Filing</span>
            <span className="w-1 h-1 rounded-md bg-white/30" />
            <span>Insights</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Microsoft Sign-In Primary */}
      <div className="flex-1 flex items-center justify-center p-8 bg-transparent">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src={LOGO_URL}
              alt="Bonsai Law" 
              className="w-20 h-20 mx-auto mb-3"
            />
            <h1 className="text-xl text-teal">
              Bonsai CaseMinder
            </h1>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold agents-title mb-2" data-testid="login-title">
              Sign in to CaseMinder
            </h2>
            <p className="text-sm text-indigo-100/70">
              Access matter management, compliance and administration tools
            </p>
          </div>

          {/* Primary: Microsoft Sign-In */}
          <div className="cm-hero rounded-2xl border border-white/10 shadow-2xl shadow-indigo-950/60">
            <MicrosoftSignInCard 
              returnUrl="/"
              title="Connect your Microsoft account"
              description="Sign in with your Microsoft 365 account to access CaseMinder, email filing, and SharePoint integration."
              className="border-0 shadow-none bg-transparent"
            />
          </div>

          {/* Secondary: Bonsai Credentials (collapsed by default) */}
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => setShowCredentialLogin(!showCredentialLogin)}
              className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5 transition-colors"
              data-testid="toggle-credential-login"
            >
              {showCredentialLogin ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide Bonsai credentials login
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Sign in with Bonsai credentials
                </>
              )}
            </button>
          </div>

          {/* Credential Login Form (collapsed) */}
          {showCredentialLogin && (
            <div className="mt-4 glass-card rounded-2xl p-6 animate-fade-in-up">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 text-sm">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@bonsai.law"
                      className="pl-9 h-10 border-slate-200 focus:border-teal focus:ring-teal rounded-md"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 text-sm">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="pl-9 pr-9 h-10 border-slate-200 focus:border-teal focus:ring-teal rounded-md"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {sessionExpired && !error && (
                  <div
                    className="p-2.5 rounded-md bg-status-amber-dim text-status-amber text-sm border border-amber-200"
                    data-testid="login-session-expired-banner"
                  >
                    Your session expired — please log in again.
                  </div>
                )}

                {error && (
                  <div
                    className="p-2.5 rounded-md bg-status-red-dim text-status-red text-sm border border-red-100"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !password}
                  className="w-full h-10 text-white text-sm rounded-md"
                  data-testid="login-button"
                >
                  {loading ? "Signing in..." : "Sign In with Credentials"}
                </Button>
              </form>
            </div>
          )}

          <p className="text-center text-xs text-slate-400 mt-8">
            Internal use only &middot; Contact IT for access
          </p>
        </div>
      </div>
    </div>
  );
}
